import { createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import Api from "../../Config/ApiConfig";

// Get all notification templates
export const getNotificationTemplates = createAsyncThunk(
  "notificationTemplates/getAll",
  async (params = {}) => {
    try {
      const { page = 1, limit = 20, search = '', category = '', isActive } = params;
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...(search && { search }),
        ...(category && category !== 'all' && { category }),
        ...(isActive !== undefined && { isActive: isActive.toString() })
      });

      const response = await Api.get(`notification-templates?${queryParams}`);
      
      if (response.data.status_code === 200) {
        return response.data;
      } else {
        toast.error(response.data.message);
        throw new Error(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to fetch templates");
      throw error;
    }
  }
);

// Get single notification template
export const getNotificationTemplate = createAsyncThunk(
  "notificationTemplates/getOne",
  async (templateId) => {
    try {
      const response = await Api.get(`notification-templates/${templateId}`);
      
      if (response.data.status_code === 200) {
        return response.data;
      } else {
        toast.error(response.data.message);
        throw new Error(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to fetch template");
      throw error;
    }
  }
);

// Create notification template
export const createNotificationTemplate = createAsyncThunk(
  "notificationTemplates/create",
  async (templateData) => {
    try {
      const response = await Api.post("notification-templates", templateData);
      
      if (response.data.status_code === 200) {
        toast.success("Template created successfully!");
        return response.data;
      } else {
        toast.error(response.data.message);
        throw new Error(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to create template");
      throw error;
    }
  }
);

// Update notification template
export const updateNotificationTemplate = createAsyncThunk(
  "notificationTemplates/update",
  async ({ templateId, templateData }) => {
    try {
      const response = await Api.put(`notification-templates/${templateId}`, templateData);
      
      if (response.data.status_code === 200) {
        toast.success("Template updated successfully!");
        return response.data;
      } else {
        toast.error(response.data.message);
        throw new Error(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update template");
      throw error;
    }
  }
);

// Delete notification template
export const deleteNotificationTemplate = createAsyncThunk(
  "notificationTemplates/delete",
  async (templateId) => {
    try {
      const response = await Api.delete(`notification-templates/${templateId}`);
      
      if (response.data.status_code === 200) {
        toast.success("Template deleted successfully!");
        return { templateId };
      } else {
        toast.error(response.data.message);
        throw new Error(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete template");
      throw error;
    }
  }
);

// Increment template usage
export const incrementTemplateUsage = createAsyncThunk(
  "notificationTemplates/incrementUsage",
  async (templateId) => {
    try {
      await Api.post(`notification-templates/${templateId}/use`);
      return { templateId };
    } catch (error) {
      // Don't show error for usage tracking, but still return templateId for state update
      console.error("Failed to track template usage:", error);
      return { templateId };
    }
  }
);

// Get templates by category
export const getTemplatesByCategory = createAsyncThunk(
  "notificationTemplates/getByCategory",
  async (category) => {
    try {
      const response = await Api.get(`notification-templates/category/${category}`);
      
      if (response.data.status_code === 200) {
        return response.data;
      } else {
        toast.error(response.data.message);
        throw new Error(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to fetch templates");
      throw error;
    }
  }
);